import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { client } from '../../api/client';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import { ConfirmDialog, Toast } from '../ui/Dialog';
import type { ConfirmOptions } from '../ui/Dialog';
import { Trash2, Edit2, Plus, Save } from 'lucide-react';
import { theme } from '../../styles/theme';
import { getApiErrorMessage } from '../../utils/apiErrors';

const SectionTitle = styled.h3`
  margin-bottom: 1.5rem;
  color: ${theme.colors.primary.navy};
  border-bottom: 2px solid ${theme.colors.primary.gold};
  padding-bottom: 0.5rem;
  display: inline-block;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: bold;
    color: ${theme.colors.primary.navy};
  }
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const BoardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const BoardItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  h4 {
    margin: 0;
    color: ${theme.colors.primary.navy};
  }

  p {
    margin: 0;
    color: ${theme.colors.neutral.gray};
    font-size: 0.9rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.neutral.gray};
  padding: 0.25rem;
  
  &:hover {
    color: ${theme.colors.primary.royalBlue};
  }
  
  &.delete:hover {
    color: ${theme.colors.error};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${({ theme }) => theme.zIndices.modalBackdrop};
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
`;

interface SiteSettings {
  count_harmonie: string;
  count_precilia: string;
  count_trommelkorps: string;
  count_popcilia: string;
  [key: string]: string;
}

interface BoardMember {
  id: string;
  name: string;
  role: string;
  order: number;
}

export const SiteInfoSettings = () => {
  const [settings, setSettings] = useState<SiteSettings>({
    count_harmonie: '',
    count_precilia: '',
    count_trommelkorps: '',
    count_popcilia: '',
  });
  const [boardMembers, setBoardMembers] = useState<BoardMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState<BoardMember | null>(null);
  const [confirm, setConfirm] = useState<ConfirmOptions | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const showConfirm = useCallback((opts: Omit<ConfirmOptions, 'onCancel'> & { onCancel?: () => void }) => {
    setConfirm({ onCancel: () => setConfirm(null), ...opts });
  }, []);

  // Form state for member modal
  const [memberName, setMemberName] = useState('');
  const [memberRole, setMemberRole] = useState('');
  const [memberOrder, setMemberOrder] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, membersRes] = await Promise.all([
        client.get('/settings'),
        client.get('/board-members'),
      ]);
      setSettings(settingsRes.data);
      setBoardMembers(membersRes.data);
    } catch (error) {
      console.error('Failed to fetch site info', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await client.post('/settings', settings);
      showToast('Instellingen opgeslagen!', 'success');
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Opslaan mislukt'), 'error');
    }
  };

  const handleDeleteMember = (id: string) => {
    showConfirm({
      title: 'Bestuurslid verwijderen',
      message: 'Weet je zeker dat je dit bestuurslid wilt verwijderen?',
      confirmLabel: 'Verwijderen',
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await client.delete(`/board-members/${id}`);
          fetchData();
        } catch (error) {
          showToast(getApiErrorMessage(error, 'Verwijderen mislukt'), 'error');
        }
      },
    });
  };

  const openMemberModal = (member?: BoardMember) => {
    if (member) {
      setEditingMember(member);
      setMemberName(member.name);
      setMemberRole(member.role);
      setMemberOrder(member.order);
    } else {
      setEditingMember(null);
      setMemberName('');
      setMemberRole('');
      setMemberOrder(boardMembers.length + 1);
    }
    setShowMemberModal(true);
  };

  const handleSaveMember = async () => {
    try {
      const data = { name: memberName, role: memberRole, order: Number(memberOrder) };
      if (editingMember) {
        await client.patch(`/board-members/${editingMember.id}`, data);
      } else {
        await client.post('/board-members', data);
      }
      setShowMemberModal(false);
      fetchData();
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Opslaan bestuurslid mislukt'), 'error');
    }
  };

  if (isLoading) return <p>Laden...</p>;

  return (
    <div>
      <Card style={{ marginBottom: '2rem' }}>
        <SectionTitle>Leden Aantallen</SectionTitle>
        <p style={{ marginBottom: '1.5rem', color: '#666' }}>
          Deze aantallen worden op de site afgerond naar het dichtstbijzijnde tiental (naar beneden).
        </p>
        <SettingsGrid>
          <FormGroup>
            <label>Harmonie</label>
            <Input
              type="number"
              value={settings.count_harmonie}
              onChange={(e) => setSettings({ ...settings, count_harmonie: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <label>Précilia</label>
            <Input
              type="number"
              value={settings.count_precilia}
              onChange={(e) => setSettings({ ...settings, count_precilia: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <label>Trommelkorps</label>
            <Input
              type="number"
              value={settings.count_trommelkorps}
              onChange={(e) => setSettings({ ...settings, count_trommelkorps: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <label>Popcilia</label>
            <Input
              type="number"
              value={settings.count_popcilia}
              onChange={(e) => setSettings({ ...settings, count_popcilia: e.target.value })}
            />
          </FormGroup>
        </SettingsGrid>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleSaveSettings}>
            <Save size={18} style={{ marginRight: '0.5rem' }} /> Opslaan
          </Button>
        </div>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <SectionTitle style={{ marginBottom: 0 }}>Bestuursleden</SectionTitle>
          <Button onClick={() => openMemberModal()}>
            <Plus size={18} style={{ marginRight: '0.5rem' }} /> Toevoegen
          </Button>
        </div>

        <BoardList>
          {boardMembers.map((member) => (
            <BoardItem key={member.id}>
              <div>
                <h4>{member.name}</h4>
                <p>{member.role}</p>
              </div>
              <ActionButtons>
                <IconButton onClick={() => openMemberModal(member)} title="Bewerk">
                  <Edit2 size={18} />
                </IconButton>
                <IconButton className="delete" onClick={() => handleDeleteMember(member.id)} title="Verwijder">
                  <Trash2 size={18} />
                </IconButton>
              </ActionButtons>
            </BoardItem>
          ))}
        </BoardList>
      </Card>

      {showMemberModal && (
        <ModalOverlay onClick={() => setShowMemberModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>{editingMember ? 'Bewerk Bestuurslid' : 'Nieuw Bestuurslid'}</h3>
            <FormGroup>
              <label>Naam</label>
              <Input value={memberName} onChange={(e) => setMemberName(e.target.value)} placeholder="Voornaam Achternaam" />
            </FormGroup>
            <FormGroup>
              <label>Functie</label>
              <Input value={memberRole} onChange={(e) => setMemberRole(e.target.value)} placeholder="vb. Voorzitter" />
            </FormGroup>
            <FormGroup>
              <label>Volgorde</label>
              <Input type="number" value={memberOrder} onChange={(e) => setMemberOrder(Number(e.target.value))} />
            </FormGroup>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
              <Button variant="ghost" onClick={() => setShowMemberModal(false)}>
                Annuleer
              </Button>
              <Button onClick={handleSaveMember}>Opslaan</Button>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}

      {confirm && <ConfirmDialog {...confirm} />}
      {toast && <Toast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />}
    </div>
  );
};
